import { PageHero, SectionTitle } from "@/components/Shared";
import { Reveal } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/Marquee";
import { Recycle, MapPin, Rocket } from "lucide-react";
import { IMAGES, LOGOS } from "@/data/site";

const values = [
  { icon: MapPin, t: "Built On A Parking Lot", d: "A real kart circuit laid out across the 150,000+ sq ft Southern Parking Lot of the Thomas A. Robinson National Stadium." },
  { icon: Recycle, t: "Made From Recycled Tyres", d: "Hundreds of recycled used car tyres form every barrier and corner — resourceful, sustainable and genuinely Bahamian." },
  { icon: Rocket, t: "Proving The Demand", d: "Every lap builds the case for a permanent, state-of-the-art karting complex in The Bahamas." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="ABOUT US"
        title="Grassroots Bahamian Motorsport"
        subtitle="Karting Bahamas is a real, hand-built go-kart track on a stadium parking lot — and the first step toward a permanent karting home in The Bahamas."
        image={IMAGES.trackDrone}
        testid="about-hero"
      />

      <section className="bg-[#050505] py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] items-start gap-14 px-5 md:grid-cols-2 md:px-10">
          <div>
            <SectionTitle eyebrow="OUR STORY" title="From A Parking Lot Up" />
          </div>
          <div className="space-y-5 text-white/60">
            <p className="leading-relaxed">
              Karting Bahamas began with a simple idea: you don't need a multi-million dollar facility to
              bring real racing to The Bahamas — you need passion, a big space, and a lot of tyres. So we
              transformed the 150,000+ square foot Southern Parking Lot of the Thomas A. Robinson National
              Stadium in Nassau into a genuine, competitive kart circuit.
            </p>
            <p className="leading-relaxed">
              The track is defined and protected entirely by hundreds of recycled used car tyres. It isn't a
              permanent, state-of-the-art complex — and that's exactly the point. It's grassroots motorsport,
              built by hand, open to everyone: tourists, locals, families and corporate teams.
            </p>
            <p className="leading-relaxed">
              Operating under the Bahamas Motor Sports Federation and in partnership with the FIA Karting club
              EduKarting Bahamas, we're building a racing culture from the ground up — and proving there's real
              demand for a permanent karting home in The Bahamas.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-obsidian py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid gap-6 md:grid-cols-3">
            {values.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.1}>
                <div className="h-full rounded-xl border border-white/10 bg-[#0A0A0A] p-8">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-kbcyan/10 text-kbcyan">
                    <v.icon className="h-7 w-7" />
                  </div>
                  <h3 className="mt-6 font-display text-3xl tracking-wide text-white">{v.t}</h3>
                  <p className="mt-3 leading-relaxed text-white/55">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FIA credential banner */}
      <section data-testid="fia-banner" className="border-y border-kbyellow/20 bg-obsidian py-16 md:py-20">
        <div className="mx-auto max-w-[1100px] px-5 text-center md:px-10">
          <span className="font-display text-lg tracking-[0.3em] text-kbyellow">OFFICIAL &amp; SANCTIONED</span>
          <h2 className="mx-auto mt-4 max-w-3xl font-display text-4xl leading-[0.95] tracking-tight text-white md:text-6xl">
            The Only FIA-Approved Motorsports Club In The Bahamas
          </h2>
          <p className="mx-auto mt-6 max-w-2xl leading-relaxed text-white/65">
            Karting Bahamas is the only official FIA-approved motorsports club in The Bahamas. Our accreditation
            connects us to a global network of 245 FIA ASN member clubs worldwide, and makes us the only club in
            the country able to issue both international and national FIA racing licenses. As the new home of
            EduKarting Bahamas, a registered non-profit, all proceeds go toward funding its youth development
            programme.
          </p>
        </div>
      </section>

      {/* The Vision */}
      <section data-testid="vision-section" className="relative overflow-hidden py-28 md:py-36">
        <img src={IMAGES.racingAction} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/75 to-[#050505]" />
        <div className="relative mx-auto max-w-[1000px] px-5 text-center md:px-10">
          <span className="font-display text-lg tracking-[0.3em] text-kbyellow">THE VISION</span>
          <h2 className="mx-auto mt-4 max-w-3xl font-display text-5xl leading-[0.9] tracking-tight text-white md:text-7xl">
            The Road To A Permanent Karting Complex
          </h2>
          <p className="mx-auto mt-6 max-w-2xl leading-relaxed text-white/65">
            Today it's a track on a parking lot. Tomorrow, we want it to be a purpose-built, state-of-the-art
            karting complex — a true home for Bahamian motorsport, youth development and world-class events.
            Every driver who arrives, signs and races helps make that future real.
          </p>
        </div>
      </section>

      {/* Partners */}
      <section className="bg-[#050505] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionTitle eyebrow="OUR PARTNERS" title="Backed By The Best" align="center" className="mb-16 text-center" />
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {[
              { src: LOGOS.full, alt: "Karting Bahamas", h: "h-16 md:h-20" },
              { src: LOGOS.edukarting, alt: "EduKarting Bahamas", h: "h-20 md:h-24" },
              { src: LOGOS.bac, alt: "Bahamas Automobile Club", h: "h-12 md:h-14" },
            ].map((p) => (
              <div key={p.alt} className="flex items-center justify-center rounded-2xl bg-white px-8 py-6 transition-transform hover:scale-[1.03]">
                <img src={p.src} alt={p.alt} className={`${p.h} w-auto`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee words={["NASSAU", "THE BAHAMAS", "MOTORSPORT", "GRASSROOTS"]} />
    </div>
  );
}
